import { Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { cn } from "../lib/utils";
import { type LivreResume, PLAN_PAR_DEFAUT } from "./contract";
import {
  ALERTE_GRISE,
  BadgeEtatLivre,
  BOUTON_CONTOUR,
  CHAMP,
  LIEN_BOUTON_PLEIN,
} from "./histoires-commun";
import { messageDEchec, useCreerLivre, useLivres } from "./histoires-requetes";
import { cleDepuisTitre, modifieEnClair, planEnClair, refusDeCle } from "./histoires-textes";
import { ConsoleShell } from "./shell";
import { Chargement, EcranDErreur, panneAffichee } from "./states";

/**
 * L'accueil du module Histoires (`/histoires`) : les livres, leur etat et la mesure H2, les
 * doubles ecrites sur le nombre de doubles `histoire` du livre (spec du 26/09/2026, section 3,
 * et amendement 1). Depuis le ticket 2 (#226), chaque livre s'ouvre, et « Nouveau livre » en
 * cree un au plan par defaut (maquette `HistoiresAccueil.dc.html`).
 *
 * La maquette montre aussi, par livre, le nombre de mots, les provenances, la declaration et
 * « Écrire la suivante » : la liste du contrat (`livreResumeSchema`) ne les porte pas, ils sont
 * sur la page du livre.
 */

/**
 * La mesure H2 en toutes lettres : les doubles ecrites sur les doubles `histoire` du livre,
 * accordees (une double, deux doubles), et un mot pour un livre qui n'a pas encore de plan.
 */
export function ecritesSur(ecrites: number, doubles: number): string {
  if (doubles === 0) return "Aucune double pour l'instant";
  return `${ecrites} ${ecrites > 1 ? "doubles écrites" : "double écrite"} sur ${doubles}`;
}

export function HistoiresAccueilPage() {
  const lecture = useLivres();
  const panne = panneAffichee(lecture);
  const [formulaire, setFormulaire] = useState<"ferme" | "ouvert" | "annule">("ferme");
  const bouton = useRef<HTMLButtonElement>(null);

  // Ferme sans creer : le focus revient au bouton qui l'a ouvert.
  useEffect(() => {
    if (formulaire === "annule") bouton.current?.focus();
  }, [formulaire]);

  return (
    <ConsoleShell connecte={lecture.isSuccess}>
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div>
          <h1 className="text-2xl font-semibold">Histoires</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Les albums illustrés pour KDP, et où en est leur texte.
          </p>
        </div>
        {lecture.isSuccess ? (
          <button
            aria-expanded={formulaire === "ouvert"}
            className={LIEN_BOUTON_PLEIN}
            onClick={() => setFormulaire("ouvert")}
            ref={bouton}
            type="button"
          >
            Nouveau livre
          </button>
        ) : null}
      </header>
      {lecture.isPending ? <Chargement quoi="des livres" /> : null}
      {panne ? <EcranDErreur error={lecture.error} quoi="les livres" /> : null}
      {lecture.data && !panne ? <Livres livres={lecture.data.livres} /> : null}
      {formulaire === "ouvert" && lecture.isSuccess ? (
        <NouveauLivre fermer={() => setFormulaire("annule")} />
      ) : null}
    </ConsoleShell>
  );
}

function Livres({ livres }: { livres: readonly LivreResume[] }) {
  const maintenant = Date.now();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base" id="histoires-livres">
          Livres
        </CardTitle>
      </CardHeader>
      <CardContent>
        {livres.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun livre pour l'instant.</p>
        ) : (
          <ul aria-labelledby="histoires-livres" className="flex flex-col">
            {livres.map((livre) => (
              <LigneLivre key={livre.id} livre={livre} maintenant={maintenant} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Une ligne par livre : son titre (le lien) et sa cle, la mesure H2 et sa barre, la date de
 * modification, l'etat. Sur telephone, les quatre s'empilent.
 */
function LigneLivre({ livre, maintenant }: { livre: LivreResume; maintenant: number }) {
  const part = livre.doubles === 0 ? 0 : Math.min(100, (livre.ecrites / livre.doubles) * 100);
  const modifie = modifieEnClair(livre.maj_le, maintenant);
  return (
    <li className="grid gap-x-6 gap-y-2 border-t border-border py-3 first:border-t-0 first:pt-0 last:pb-0 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto_auto] md:items-center">
      <div className="min-w-0">
        <Link
          className="font-semibold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          params={{ id: String(livre.id) }}
          to="/histoires/livres/$id"
        >
          {livre.titre}
        </Link>
        <p className="text-sm text-muted-foreground">{livre.cle}</p>
      </div>
      <div className="min-w-0">
        <p className="text-sm tabular-nums">{ecritesSur(livre.ecrites, livre.doubles)}</p>
        <span
          aria-hidden="true"
          className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-muted"
        >
          <span className="block h-full bg-foreground" style={{ width: `${part}%` }} />
        </span>
      </div>
      <p className="text-sm text-muted-foreground">
        {modifie === "" ? null : `modifié ${modifie}`}
      </p>
      <span>
        <BadgeEtatLivre etat={livre.etat} />
      </span>
    </li>
  );
}

/**
 * « Nouveau livre » : le titre, et la cle proposee depuis lui tant qu'on ne la touche pas. La
 * cle est validee par le schema du contrat avant l'envoi ; le livre cree s'ouvre.
 */
function NouveauLivre({ fermer }: { fermer: () => void }) {
  const navigate = useNavigate();
  const creer = useCreerLivre();
  const [titre, setTitre] = useState("");
  const [cleSaisie, setCleSaisie] = useState<string | null>(null);
  const [tente, setTente] = useState(false);
  const champTitre = useRef<HTMLInputElement>(null);
  const idTitre = useId();
  const idCle = useId();

  useEffect(() => {
    champTitre.current?.focus();
  }, []);

  const cle = cleSaisie ?? cleDepuisTitre(titre);
  const refusTitre = titre.trim() === "" ? "Le titre est vide." : null;
  const refusCle = refusDeCle(cle);
  const montrerTitre = tente && refusTitre !== null;
  const montrerCle = (tente || cleSaisie !== null) && refusCle !== null;

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setTente(true);
    if (refusTitre !== null || refusCle !== null || creer.isPending) return;
    creer.mutate(
      { cle, titre: titre.trim() },
      {
        onSuccess: (reponse) => {
          void navigate({
            to: "/histoires/livres/$id",
            params: { id: String(reponse.livre.id) },
          });
        },
      },
    );
  };

  return (
    <section
      aria-labelledby="histoires-nouveau-livre"
      className="max-w-3xl rounded-lg border border-border bg-card p-5 text-card-foreground"
    >
      <h2 className="mb-4 text-base font-semibold" id="histoires-nouveau-livre">
        Nouveau livre
      </h2>
      <form className="flex flex-col gap-4" noValidate onSubmit={envoyer}>
        <div className="grid items-start gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold" htmlFor={idTitre}>
              Titre
            </label>
            <input
              aria-describedby={montrerTitre ? `${idTitre}-refus` : undefined}
              aria-invalid={montrerTitre || undefined}
              className={CHAMP}
              id={idTitre}
              maxLength={200}
              onChange={(evenement) => setTitre(evenement.target.value)}
              placeholder="Le titre de travail, modifiable ensuite"
              ref={champTitre}
              value={titre}
            />
            {montrerTitre ? (
              <p className="text-sm text-destructive" id={`${idTitre}-refus`}>
                {refusTitre}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold" htmlFor={idCle}>
              Clé
            </label>
            <input
              aria-describedby={montrerCle ? `${idCle}-aide ${idCle}-refus` : `${idCle}-aide`}
              aria-invalid={montrerCle || undefined}
              autoCapitalize="none"
              autoCorrect="off"
              className={cn(CHAMP, "font-mono")}
              id={idCle}
              maxLength={120}
              onChange={(evenement) => setCleSaisie(evenement.target.value)}
              placeholder="se remplit depuis le titre"
              spellCheck={false}
              value={cle}
            />
            <p className="text-xs text-muted-foreground" id={`${idCle}-aide`}>
              Minuscules, chiffres et tirets : le nom du dossier du livre sur le PC. Elle ne change
              plus ensuite.
            </p>
            {montrerCle ? (
              <p className="text-sm text-destructive" id={`${idCle}-refus`}>
                {refusCle}
              </p>
            ) : null}
          </div>
        </div>
        <p className={ALERTE_GRISE}>
          <strong className="font-semibold">Le plan posé :</strong> {planEnClair(PLAN_PAR_DEFAUT)}{" "}
          de 21,6 × 21,6 cm. Un autre plan se pose par{" "}
          <code className="rounded border border-border bg-card px-1 font-mono text-xs">
            histoire console plan
          </code>
          , comme les 16 doubles de l'album du deuil. Le brief et les intentions se remplissent
          ensuite, dans l'onglet Brief ou par Claude.
        </p>
        {creer.isError ? (
          <p className="text-sm text-destructive" role="alert">
            {messageDEchec(creer.error)}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button
            aria-disabled={creer.isPending || undefined}
            className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            type="submit"
          >
            Créer le livre
          </Button>
          <button className={BOUTON_CONTOUR} onClick={fermer} type="button">
            Annuler
          </button>
        </div>
      </form>
    </section>
  );
}
